import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { Bell, Check, AlertTriangle, Info, AlertCircle, Loader2 } from 'lucide-react';
import { notifsApi, type SDNotification } from '../api/notifications';
import { fmtTime } from '../lib/format';

const POLL_MS = 20_000;  // обновляем счётчик каждые 20 сек

export function NotificationsBell() {
  const [count, setCount] = useState(0);
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState<SDNotification[]>([]);
  const [loading, setLoading] = useState(false);
  const wrapRef = useRef<HTMLDivElement>(null);

  const refreshCount = async () => {
    try { setCount(await notifsApi.unreadCount()); } catch {}
  };

  const loadList = async () => {
    setLoading(true);
    try { setItems(await notifsApi.list()); }
    finally { setLoading(false); }
  };

  useEffect(() => {
    refreshCount();
    const t = setInterval(refreshCount, POLL_MS);
    return () => clearInterval(t);
  }, []);

  useEffect(() => {
    if (open) loadList();
  }, [open]);

  // Закрытие по клику вне
  useEffect(() => {
    const onClick = (e: MouseEvent) => {
      if (!wrapRef.current?.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, []);

  const onItemClick = async (n: SDNotification) => {
    if (!n.read_at) {
      await notifsApi.markRead(n.id);
      refreshCount();
      setItems(items.map(x => x.id === n.id ? { ...x, read_at: new Date().toISOString() } : x));
    }
  };

  const onMarkAll = async () => {
    await notifsApi.markAllRead();
    refreshCount();
    setItems(items.map(x => x.read_at ? x : { ...x, read_at: new Date().toISOString() }));
  };

  return (
    <div ref={wrapRef} className="relative">
      <button onClick={() => setOpen(o => !o)}
        className="relative p-2 text-slate-400 hover:text-blue-400 transition hover:bg-slate-800 rounded-full"
        title="Уведомления">
        <Bell size={18} />
        {count > 0 && (
          <span className="absolute -top-0.5 -right-0.5 bg-red-600 text-white text-[10px] font-bold rounded-full min-w-[16px] h-[16px] flex items-center justify-center px-1">
            {count > 99 ? '99+' : count}
          </span>
        )}
      </button>

      {open && (
        <div className="absolute right-0 top-full mt-2 w-96 bg-slate-900 border border-slate-700 rounded-xl shadow-2xl z-50 max-h-[70vh] overflow-y-auto">
          <header className="flex items-center justify-between px-4 py-3 border-b border-slate-800 sticky top-0 bg-slate-900">
            <h3 className="font-bold text-white text-sm">Уведомления</h3>
            {count > 0 && (
              <button onClick={onMarkAll} className="text-[11px] text-slate-400 hover:text-blue-400 inline-flex items-center gap-1">
                <Check size={11} /> Прочитать все
              </button>
            )}
          </header>
          {loading ? (
            <div className="p-6 text-center"><Loader2 className="animate-spin text-blue-400 inline" /></div>
          ) : items.length === 0 ? (
            <div className="p-6 text-center text-sm text-slate-500">Уведомлений нет</div>
          ) : (
            <ul>
              {items.map(n => <NotifItem key={n.id} n={n} onClick={onItemClick} />)}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}

const SEVERITY_ICON: Record<string, any> = {
  info: Info,
  warn: AlertTriangle,
  critical: AlertCircle,
};
const SEVERITY_COLOR: Record<string, string> = {
  info: 'text-blue-400',
  warn: 'text-yellow-400',
  critical: 'text-red-400',
};

function NotifItem({ n, onClick }: { n: SDNotification; onClick: (n: SDNotification) => void }) {
  const Icon = SEVERITY_ICON[n.severity] || Info;
  const inner = (
    <div onClick={() => onClick(n)}
      className={`flex items-start gap-3 px-4 py-3 border-b border-slate-800 hover:bg-slate-800/40 cursor-pointer transition ${
        n.read_at ? 'opacity-60' : ''
      }`}>
      <Icon size={14} className={`mt-0.5 shrink-0 ${SEVERITY_COLOR[n.severity]}`} />
      <div className="flex-1 min-w-0">
        <div className="flex items-center justify-between gap-2">
          <span className="text-sm font-medium text-white truncate">{n.title}</span>
          {!n.read_at && <span className="w-2 h-2 bg-blue-500 rounded-full shrink-0" />}
        </div>
        <p className="text-xs text-slate-400 line-clamp-2">{n.body}</p>
        <p className="text-[10px] text-slate-600 mt-1">{fmtTime(n.created_at)}</p>
      </div>
    </div>
  );
  return n.ticket_id
    ? <li><Link to={`/tickets/${n.ticket_id}`}>{inner}</Link></li>
    : <li>{inner}</li>;
}
